import React from 'react';
import { Box, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';

import { formatDate } from '@/lib/formatters';

const SYNC_DATE_PICKER_FORMAT = 'dd/MM/yyyy';

export type SyncDateRange = {
  dateFrom: Date | null;
  dateTo: Date | null;
};

export function parseSyncDateValue(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toSyncDatePayload(range: SyncDateRange): {
  dateFrom?: string;
  dateTo?: string;
} {
  const payload: { dateFrom?: string; dateTo?: string } = {};
  if (range.dateFrom) {
    payload.dateFrom = formatDate(range.dateFrom);
  }
  if (range.dateTo) {
    payload.dateTo = formatDate(range.dateTo);
  }
  return payload;
}

export function isValidSyncDateRange(range: SyncDateRange): boolean {
  if (range.dateFrom && range.dateTo && range.dateFrom > range.dateTo) {
    return false;
  }
  return true;
}

export function syncDateRangeFromLog(
  dateFrom: string | null | undefined,
  dateTo: string | null | undefined,
): SyncDateRange {
  return {
    dateFrom: parseSyncDateValue(dateFrom),
    dateTo: parseSyncDateValue(dateTo),
  };
}

export function SyncDateRangePicker({
  value,
  onChange,
  compact = false,
}: {
  value: SyncDateRange;
  onChange: (value: SyncDateRange) => void;
  compact?: boolean;
}) {
  return (
    <Box sx={{ mb: compact ? 1 : 2 }}>
      {!compact && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Sync date range
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Optional. Leave empty to use automatic incremental sync dates.
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <DatePicker
            label="Date from"
            format={SYNC_DATE_PICKER_FORMAT}
            value={value.dateFrom}
            onChange={(date) => onChange({ ...value, dateFrom: date })}
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 180 },
              },
            }}
          />
          <DatePicker
            label="Date to"
            format={SYNC_DATE_PICKER_FORMAT}
            value={value.dateTo}
            onChange={(date) => onChange({ ...value, dateTo: date })}
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 180 },
              },
            }}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );
}
